import { useEffect, useState } from "react";
import {
  Button,
  Card,
  CardContent,
  Chip,
  Grid,
  Paper,
  Rating,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import { collection, getDocs } from "firebase/firestore";
import { db } from "../lib/firebase";
import { api } from "../lib/api";
import { formatJalaliTs, formatMoney, formatNumber, localized } from "../lib/format";
import { useT } from "../i18n";
import { ConfirmDialog } from "../components/ConfirmDialog";
import type { StringKey } from "../i18n/fa";

/** Stored hall types are enum codes; the admin should read them, not decode them. */
const HALL_TYPE_KEY: Record<string, StringKey> = {
  luxury: "hall_type_luxury",
  standard: "hall_type_standard",
  garden: "hall_type_garden",
  vip_salon: "hall_type_vip_salon",
};

interface AdminKpis {
  publishedHalls: number;
  hallsAwaitingReview: number;
  confirmedBookings: number;
  openTickets: number;
}

interface PendingHall {
  id: string;
  name: Record<string, string>;
  cityId: string;
  hallType: string;
  capacity?: { total: number };
}

interface PayoutRun {
  orgId: string;
  payoutId: string;
  grossMinor: number;
  commissionMinor: number;
  netMinor: number;
  status?: "pending" | "paid";
}

interface PendingReview {
  id: string;
  hallId: string;
  rating: number;
  text?: string;
  createdAt?: string | { _seconds: number };
}

export function AdminPage() {
  const [kpis, setKpis] = useState<AdminKpis | null>(null);
  const [pending, setPending] = useState<PendingHall[]>([]);
  const [payouts, setPayouts] = useState<PayoutRun[]>([]);
  const [reviews, setReviews] = useState<PendingReview[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [settling, setSettling] = useState(false);
  // Money moves and rejections wait for an explicit "yes" (see ConfirmDialog).
  const [confirm, setConfirm] = useState<
    | { kind: "settle" }
    | { kind: "paid"; payout: PayoutRun }
    | { kind: "rejectHall"; hall: PendingHall }
    | { kind: "rejectReview"; review: PendingReview }
    | null
  >(null);
  // cityId → localized city name (cities are public reference data).
  const [cities, setCities] = useState<Record<string, Record<string, string>>>({});
  const t = useT();

  function load() {
    api<AdminKpis>("/admin/dashboard").then(setKpis).catch((e) => setError(e.message));
    api<{ items: PendingHall[] }>("/admin/halls/pending")
      .then((r) => setPending(r.items))
      .catch(() => setPending([]));
    api<{ items: PendingReview[] }>("/admin/reviews")
      .then((r) => setReviews(r.items))
      .catch(() => setReviews([]));
  }

  useEffect(load, []);

  useEffect(() => {
    getDocs(collection(db, "cities"))
      .then((snap) =>
        setCities(
          Object.fromEntries(
            snap.docs.map((d) => [d.id, (d.data().name as Record<string, string>) ?? {}]),
          ),
        ),
      )
      .catch(() => setCities({}));
  }, []);

  async function review(hallId: string, decision: "approve" | "reject") {
    setBusyId(hallId);
    try {
      await api(`/admin/halls/${hallId}/review`, { method: "POST", body: { decision } });
      load();
    } catch (e) {
      setError(e instanceof Error ? e.message : t("common_error"));
    } finally {
      setBusyId(null);
    }
  }

  async function runSettlement() {
    setSettling(true);
    setError(null);
    try {
      const r = await api<{ payouts: PayoutRun[] }>("/admin/payouts/run", {
        method: "POST",
        idempotent: true,
      });
      setPayouts(r.payouts.map((p) => ({ ...p, status: "pending" })));
    } catch (e) {
      setError(e instanceof Error ? e.message : t("common_error"));
    } finally {
      setSettling(false);
    }
  }

  async function markPaid(payout: PayoutRun) {
    setBusyId(payout.payoutId);
    try {
      await api(`/admin/payouts/${payout.orgId}/${payout.payoutId}/mark-paid`, {
        method: "POST",
        idempotent: true,
      });
      setPayouts((prev) =>
        prev.map((p) => (p.payoutId === payout.payoutId ? { ...p, status: "paid" } : p)),
      );
    } catch (e) {
      setError(e instanceof Error ? e.message : t("common_error"));
    } finally {
      setBusyId(null);
    }
  }

  async function moderate(target: PendingReview, decision: "publish" | "reject") {
    setBusyId(target.id);
    try {
      await api("/admin/reviews/moderate", {
        method: "POST",
        body: { hallId: target.hallId, reviewId: target.id, decision },
      });
      setReviews((prev) => prev.filter((r) => r.id !== target.id));
    } catch (e) {
      setError(e instanceof Error ? e.message : t("common_error"));
    } finally {
      setBusyId(null);
    }
  }

  async function runConfirmed() {
    const c = confirm;
    if (!c) return;
    try {
      if (c.kind === "settle") await runSettlement();
      else if (c.kind === "paid") await markPaid(c.payout);
      else if (c.kind === "rejectHall") await review(c.hall.id, "reject");
      else await moderate(c.review, "reject");
    } finally {
      setConfirm(null);
    }
  }

  const confirmText =
    confirm?.kind === "settle"
      ? { title: t("confirm_settle_title"), body: t("confirm_settle_body"), label: t("admin_run_settlement") }
      : confirm?.kind === "paid"
        ? {
            title: t("confirm_paid_title"),
            body: t("confirm_paid_body", {
              amount: formatMoney(confirm.payout.netMinor),
              org: confirm.payout.orgId,
            }),
            label: t("admin_mark_paid"),
          }
        : confirm?.kind === "rejectHall"
          ? {
              title: t("confirm_reject_hall_title"),
              body: t("confirm_reject_hall_body", { name: localized(confirm.hall.name) }),
              label: t("common_reject"),
            }
          : confirm?.kind === "rejectReview"
            ? {
                title: t("confirm_reject_review_title"),
                body: t("confirm_reject_review_body"),
                label: t("common_reject"),
              }
            : null;

  const cards = [
    { label: t("admin_kpi_published"), value: kpis?.publishedHalls },
    { label: t("admin_kpi_awaiting"), value: kpis?.hallsAwaitingReview },
    { label: t("admin_kpi_confirmed"), value: kpis?.confirmedBookings },
    { label: t("admin_kpi_tickets"), value: kpis?.openTickets },
  ];

  return (
    <>
      <Typography variant="h5" mb={2}>
        {t("nav_admin")}
      </Typography>
      {error && <Typography color="error">{error}</Typography>}
      <Grid container spacing={2}>
        {cards.map((card) => (
          <Grid item xs={12} sm={6} md={3} key={card.label}>
            <Card>
              <CardContent>
                <Typography color="text.secondary" variant="body2">
                  {card.label}
                </Typography>
                <Typography variant="h4" fontWeight={700}>
                  {card.value === undefined ? "…" : formatNumber(card.value)}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      <Typography variant="h6" mt={4} mb={1}>
        {t("admin_pending_halls_title")}
      </Typography>
      <Paper variant="outlined">
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>{t("admin_col_name")}</TableCell>
              <TableCell>{t("admin_col_city")}</TableCell>
              <TableCell>{t("admin_col_type")}</TableCell>
              <TableCell>{t("admin_col_capacity")}</TableCell>
              <TableCell />
            </TableRow>
          </TableHead>
          <TableBody>
            {pending.map((hall) => (
              <TableRow key={hall.id} hover>
                <TableCell>{localized(hall.name)}</TableCell>
                <TableCell>
                  <Chip size="small" label={cities[hall.cityId] ? localized(cities[hall.cityId]) : hall.cityId} />
                </TableCell>
                <TableCell>{HALL_TYPE_KEY[hall.hallType] ? t(HALL_TYPE_KEY[hall.hallType]) : hall.hallType}</TableCell>
                <TableCell>{hall.capacity?.total ? formatNumber(hall.capacity.total) : "—"}</TableCell>
                <TableCell align="right">
                  <Stack direction="row" spacing={1} justifyContent="flex-end">
                    <Button
                      size="small"
                      variant="contained"
                      disabled={busyId === hall.id}
                      onClick={() => review(hall.id, "approve")}
                    >
                      {t("admin_approve_publish")}
                    </Button>
                    <Button
                      size="small"
                      color="error"
                      disabled={busyId === hall.id}
                      onClick={() => setConfirm({ kind: "rejectHall", hall })}
                    >
                      {t("common_reject")}
                    </Button>
                  </Stack>
                </TableCell>
              </TableRow>
            ))}
            {pending.length === 0 && (
              <TableRow>
                <TableCell colSpan={5}>
                  <Typography color="text.secondary" textAlign="center" py={2}>
                    {t("admin_empty_pending")}
                  </Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </Paper>

      <Stack direction="row" alignItems="center" justifyContent="space-between" mt={4} mb={1}>
        <Typography variant="h6">{t("admin_settlement_title")}</Typography>
        <Button variant="contained" disabled={settling} onClick={() => setConfirm({ kind: "settle" })}>
          {settling ? t("admin_running") : t("admin_run_settlement")}
        </Button>
      </Stack>
      <Paper variant="outlined">
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>{t("admin_col_org")}</TableCell>
              <TableCell>{t("admin_col_gross")}</TableCell>
              <TableCell>{t("admin_col_commission")}</TableCell>
              <TableCell>{t("admin_col_net")}</TableCell>
              <TableCell>{t("common_status")}</TableCell>
              <TableCell />
            </TableRow>
          </TableHead>
          <TableBody>
            {payouts.map((payout) => (
              <TableRow key={payout.payoutId} hover>
                <TableCell>{payout.orgId}</TableCell>
                <TableCell>{formatMoney(payout.grossMinor)}</TableCell>
                <TableCell>{formatMoney(payout.commissionMinor)}</TableCell>
                <TableCell>{formatMoney(payout.netMinor)}</TableCell>
                <TableCell>
                  <Chip
                    size="small"
                    label={payout.status === "paid" ? t("payouts_status_paid") : t("payouts_status_pending")}
                    color={payout.status === "paid" ? "success" : "warning"}
                  />
                </TableCell>
                <TableCell align="right">
                  {payout.status !== "paid" && (
                    <Button
                      size="small"
                      variant="contained"
                      disabled={busyId === payout.payoutId}
                      onClick={() => setConfirm({ kind: "paid", payout })}
                    >
                      {t("admin_mark_paid")}
                    </Button>
                  )}
                </TableCell>
              </TableRow>
            ))}
            {payouts.length === 0 && (
              <TableRow>
                <TableCell colSpan={6}>
                  <Typography color="text.secondary" textAlign="center" py={2}>
                    {t("admin_empty_payouts")}
                  </Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </Paper>

      <Typography variant="h6" mt={4} mb={1}>
        {t("admin_reviews_title")}
      </Typography>
      <Paper variant="outlined">
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>{t("admin_col_hall")}</TableCell>
              <TableCell>{t("admin_col_rating")}</TableCell>
              <TableCell>{t("admin_col_text")}</TableCell>
              <TableCell>{t("admin_col_date")}</TableCell>
              <TableCell />
            </TableRow>
          </TableHead>
          <TableBody>
            {reviews.map((item) => (
              <TableRow key={item.id} hover>
                <TableCell>{item.hallId}</TableCell>
                <TableCell>
                  <Rating value={item.rating} readOnly size="small" />
                </TableCell>
                <TableCell sx={{ maxWidth: 320 }}>{item.text || "—"}</TableCell>
                <TableCell>{formatJalaliTs(item.createdAt)}</TableCell>
                <TableCell align="right">
                  <Stack direction="row" spacing={1} justifyContent="flex-end">
                    <Button
                      size="small"
                      variant="contained"
                      disabled={busyId === item.id}
                      onClick={() => moderate(item, "publish")}
                    >
                      {t("common_publish")}
                    </Button>
                    <Button
                      size="small"
                      color="error"
                      disabled={busyId === item.id}
                      onClick={() => setConfirm({ kind: "rejectReview", review: item })}
                    >
                      {t("common_reject")}
                    </Button>
                  </Stack>
                </TableCell>
              </TableRow>
            ))}
            {reviews.length === 0 && (
              <TableRow>
                <TableCell colSpan={5}>
                  <Typography color="text.secondary" textAlign="center" py={2}>
                    {t("admin_empty_reviews")}
                  </Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </Paper>
      <ConfirmDialog
        open={confirmText !== null}
        title={confirmText?.title ?? ""}
        body={confirmText?.body ?? ""}
        confirmLabel={confirmText?.label}
        tone={confirm?.kind === "rejectHall" || confirm?.kind === "rejectReview" ? "error" : "primary"}
        busy={settling || busyId !== null}
        onConfirm={runConfirmed}
        onClose={() => setConfirm(null)}
      />
    </>
  );
}
